// Verifying the email of an account: the screen that follows signing up (and signing in with a pending
// account), the page the link of the email lands on, and the public request for a new link.
//
// Nothing is confirmed before the system confirms it: «sent» is said only after an email went out, a failed
// send keeps the account pending with the way to try again (no new sign-up), and the resend waits the real
// seconds the service asked for. The address is shown only here, inside the journey of the person who has the
// password; the public request answers the same for every address.
import { Link, useSearch } from '@tanstack/react-router';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { Form } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { pendingCall, runAuth } from '../auth-call';
import { authClient } from '../auth-client';
import { useAuthForm } from '../auth-form';
import { CooldownNotice, FormError, SubmitButton, useCooldown } from '../forms';
import { Button, buttonClass, Field, Message, Page } from '../ui';
import { readForm, verificationCallback } from '../util';

/** The wait before another email can be asked for: the sentence with the seconds, and a polite note when it ends. */
function ResendWait({ cooldown }: { cooldown: ReturnType<typeof useCooldown> }) {
  const { t } = useTranslation();
  const [total, setTotal] = useState(0);
  // The first count of a wait is what a screen reader hears; the seconds that follow are only drawn.
  useEffect(() => {
    if (cooldown.active) setTotal((seconds) => seconds || cooldown.remaining);
    else setTotal(0);
  }, [cooldown.active, cooldown.remaining]);
  if (cooldown.active)
    return (
      <p className="text-sm text-ink-muted">
        {t('verify.wait')}
        <span className="sr-only"> {t('forms.limit.seconds', { count: total || cooldown.remaining })}</span>
        <span aria-hidden="true"> {t('forms.limit.seconds', { count: cooldown.remaining })}</span>
      </p>
    );
  return (
    <p role="status" className="text-sm font-bold empty:hidden">
      {cooldown.over ? t('verify.over') : ''}
    </p>
  );
}

/**
 * «Verifica tu correo electrónico»: what to do next, resending (after the wait, and confirmed only once the
 * email went out), the help for an email that does not arrive and the correction of the address. The person
 * is not signed in: the two actions use the password they just typed, which lives in this screen only.
 */
export function VerificationScreen({
  email,
  password,
  next,
  state,
  wait,
  onBack,
}: {
  email: string;
  password: string;
  /** The page the person wanted before (an invitation, say). */
  next?: string;
  /**
   * What is known of the email: `sent` (it went out just now), `notSent` (the account exists but the first
   * email could not go out) or `earlier` (the person came back to an account that was pending already).
   */
  state: 'sent' | 'notSent' | 'earlier';
  /** Seconds before another email can be asked for, as the service said. */
  wait?: number;
  onBack?: () => void;
}) {
  const { t } = useTranslation();
  const form = useAuthForm();
  const interval = useCooldown();
  const [address, setAddress] = useState(email);
  const [status, setStatus] = useState(state);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const noticeBox = useRef<HTMLDivElement>(null);
  const correctButton = useRef<HTMLButtonElement>(null);
  const callbackURL = verificationCallback(next);
  const screen = useRef<HTMLDivElement>(null);
  // This replaces the form the person was in (it stays hidden): the heading takes the focus, so that it is announced.
  useEffect(() => {
    const timer = window.setTimeout(() => screen.current?.parentElement?.querySelector('h1')?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, []);

  const { start } = interval;
  // biome-ignore lint/correctness/useExhaustiveDependencies: the wait that came with the first answer is started once.
  useEffect(() => {
    if (wait) start(wait);
  }, []);

  /** What every answer of the two actions does; returns true when the email went out. */
  function settle(
    outcome: Awaited<ReturnType<typeof pendingCall>>,
  ): outcome is { kind: 'ok'; wait?: number } {
    if (outcome.kind === 'limit' && outcome.seconds !== null) {
      // The interval between two emails: not a failure, only the time left.
      form.show({ kind: 'ok' });
      setNotice(null);
      interval.start(outcome.seconds);
      return false;
    }
    if (outcome.kind !== 'ok') {
      setNotice(null);
      form.show(outcome, {
        sendFailed: t('verify.sendFailed'),
        credentials: t('verify.credentials'),
      });
      return false;
    }
    form.show(outcome);
    setStatus('sent');
    interval.start(outcome.wait ?? 60);
    return true;
  }

  async function resend() {
    await form.run(async () => {
      const outcome = await pendingCall('resend', { email: address, password, callbackURL });
      // Said only now that the system has sent it.
      if (settle(outcome)) setNotice(t('verify.resent', { email: address }));
    });
  }

  async function correct(event: FormEvent<HTMLFormElement>) {
    const formElement = event.currentTarget;
    const { newEmail = '' } = readForm(event);
    await form.run(async () => {
      const outcome = await pendingCall('change-email', { email: address, password, newEmail, callbackURL });
      if (!settle(outcome)) return;
      const corrected = newEmail.trim().toLowerCase();
      setAddress(corrected);
      setNotice(t('verify.corrected', { email: corrected }));
      setEditing(false);
      formElement.reset();
      // The form that had the focus is hidden: the confirmation takes it.
      window.setTimeout(() => noticeBox.current?.focus(), 0);
    });
  }

  const waiting = interval.active || form.cooldown.active;
  return (
    <Page title={t('verify.title')} brand>
      <div ref={screen} className="flex flex-col gap-5">
        {form.problem && <FormError message={form.problem.message} attempt={form.problem.attempt} />}
        <CooldownNotice cooldown={form.cooldown} seconds={form.waitSeconds} />
        <div ref={noticeBox} tabIndex={-1}>
          {notice ? (
            <Message tone="ok">{notice}</Message>
          ) : status === 'notSent' ? (
            <p className="font-bold">{t('verify.notSent', { email: address })}</p>
          ) : (
            <p>{t(status === 'sent' ? 'verify.sent' : 'verify.pendingLogin', { email: address })}</p>
          )}
        </div>
        <p>{t('verify.spam')}</p>
        <div className="flex flex-wrap items-center gap-3">
          <Button onPress={resend} isDisabled={form.busy || waiting}>
            {form.busy && !editing ? t('verify.resending') : t('verify.resend')}
          </Button>
          <Button
            ref={correctButton}
            variant="secondary"
            aria-expanded={editing}
            aria-controls="corregir-correo"
            onPress={() => {
              setEditing((open) => !open);
              if (!editing)
                window.setTimeout(
                  () => document.querySelector<HTMLInputElement>('#corregir-correo input')?.focus(),
                  0,
                );
            }}
          >
            {t('verify.correct')}
          </Button>
        </div>
        <span role="status" className="sr-only">
          {form.busy && !editing ? t('verify.resending') : ''}
        </span>
        <ResendWait cooldown={interval} />
        <div id="corregir-correo" hidden={!editing}>
          {/* Kept in place (hidden) when closed: a button that was sending must not vanish with its form. */}
          {
            <Form onSubmit={correct} validationBehavior="native" className="flex flex-col gap-4">
              <p>{t('verify.correctHint')}</p>
              <Field
                name="newEmail"
                type="email"
                label={t('verify.newEmail')}
                autoComplete="off"
                spellCheck="false"
                isRequired
              />
              <div className="flex flex-wrap items-center gap-3">
                <SubmitButton
                  label={t('verify.correctSubmit')}
                  busyLabel={t('verify.correctSubmitting')}
                  busy={form.busy}
                  waiting={form.cooldown.active}
                />
                <Button
                  variant="secondary"
                  onPress={() => {
                    setEditing(false);
                    window.setTimeout(() => correctButton.current?.focus(), 0);
                  }}
                >
                  {t('verify.cancel')}
                </Button>
              </div>
            </Form>
          }
        </div>
      </div>
      <p className="mt-6">
        {onBack ? (
          <Button variant="secondary" onPress={onBack}>
            {t('verify.back')}
          </Button>
        ) : (
          <Link to="/login" search={{ redirect: next }}>
            {t('verify.back')}
          </Link>
        )}
      </p>
    </Page>
  );
}

/**
 * Where the link of the email lands. The link says what happened: it verified the account (`estado=verificada`),
 * the account already was (`estado=ya-verificada`), or the link is not valid, used on a changed address or
 * expired (`error=…`). A visit with none of that does not claim anything.
 */
export function VerifyEmailPage() {
  const { t } = useTranslation();
  const { estado, error, next } = useSearch({ from: '/verify-email' });
  const state = error
    ? 'invalid'
    : estado === 'ya-verificada'
      ? 'already'
      : estado === 'verificada'
        ? 'ok'
        : 'none';
  const login = (
    <Link to="/login" search={{ redirect: next }} className={buttonClass('primary')}>
      {t('verify.done.login')}
    </Link>
  );
  const request = (
    <Link
      to="/resend-verification"
      search={{ redirect: next }}
      className={buttonClass(state === 'invalid' ? 'primary' : 'secondary')}
    >
      {t('verify.done.request')}
    </Link>
  );
  return (
    <Page title={t(`verify.done.${state}Title`)} brand>
      <div className="flex flex-col gap-5">
        {state === 'invalid' ? (
          <FormError message={t('verify.done.invalid')} attempt={0} />
        ) : (
          <p>{t(`verify.done.${state}`)}</p>
        )}
        <p className="flex flex-wrap gap-3">
          {state === 'invalid' || state === 'none' ? request : login}
          {state === 'none' && login}
        </p>
      </div>
    </Page>
  );
}

/**
 * Asking for a new link without being signed in. The answer is the same for every address: it never says
 * whether an account exists or what state it is in, and a failed send is not announced (it would tell).
 */
export function ResendVerificationPage() {
  const { t } = useTranslation();
  const { redirect } = useSearch({ from: '/resend-verification' });
  const form = useAuthForm();
  const interval = useCooldown();
  const [done, setDone] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    const { email = '' } = readForm(event);
    await form.run(async () => {
      const outcome = await runAuth((fetchOptions) =>
        authClient.sendVerificationEmail({
          email,
          callbackURL: verificationCallback(redirect),
          fetchOptions,
        }),
      );
      if (form.show(outcome)) return;
      setDone(true);
      interval.start(outcome.kind === 'ok' && outcome.wait ? outcome.wait : 60);
    });
  }

  return (
    <Page title={t('verify.request.title')} brand>
      <Form onSubmit={submit} validationBehavior="native" className="flex flex-col gap-5">
        {form.problem && <FormError message={form.problem.message} attempt={form.problem.attempt} />}
        <CooldownNotice cooldown={form.cooldown} seconds={form.waitSeconds} />
        {done && <Message tone="ok">{t('verify.request.sent')}</Message>}
        <p>{t('verify.request.intro')}</p>
        <Field
          name="email"
          type="email"
          label={t('verify.request.email')}
          autoComplete="username"
          isRequired
        />
        <SubmitButton
          label={t('verify.request.submit')}
          busyLabel={t('verify.request.submitting')}
          busy={form.busy}
          waiting={form.cooldown.active || interval.active}
        />
        <ResendWait cooldown={interval} />
      </Form>
      <p className="mt-6">
        <Link to="/login" search={{ redirect }}>
          {t('verify.back')}
        </Link>
      </p>
    </Page>
  );
}
